<script setup>
  import {ref} from "vue";

  import ComponentConfirmationDialog from "@components/Dialogs/ConfirmationDialog.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import {router, usePage} from "@inertiajs/vue3";
  import {MedButton, MedLink} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";

  defineOptions({
    name: "ComponentCommentList",
  });

  const props = defineProps({
    patientId: {
      type: String,
      required: true,
    },
    recordId: {
      type: String,
      required: true,
    },
    comments: {
      type: Array,
      required: true,
    },
    tight: {
      type: Boolean,
      default: false,
    },
  });

  defineEmits(["showAll"]);

  const pageProperties = usePage().props;
  const refConfirmRemove = ref(null);

  function isOwnComment(comment) {
    return comment.user_id === pageProperties.client?.users[0]?.id;
  }

  function confirmRemove(comment) {
    refConfirmRemove.value.open(comment);
  }

  function remove(comment) {
    router.delete(route("comments.destroy", {patient: props.patientId, record: props.recordId, comment: comment.id}), {
      preserveScroll: true,
      only: ["flash", "errors", "comments", "notifications", "checks"],
    });
  }
</script>

<template>
  <div>
    <div v-if="tight && comments.length > 2" class="mb-2 text-center">
      <MedLink
        as="button"
        variant="primary"
        :data-test="getTestId('comment-list-show-all-link')"
        @click.prevent="$emit('showAll')"
      >
        alle anzeigen
      </MedLink>
    </div>

    <ul class="mt-2 space-y-6" :class="{'text-sm': tight}">
      <li v-if="comments.length === 0" class="cursor-default" :data-test="getTestId('comment-list-empty')">
        Bisher keine Kommentare
      </li>

      <li
        v-for="(comment, index) in tight ? comments.slice(-2) : comments"
        v-else
        :key="index"
        :data-test="getTestId(`comment-list-${index}`)"
      >
        <div v-if="isOwnComment(comment)" :class="cn('relative rounded-xl bg-gray-200', {'p-4': !tight, 'p-2': tight})">
          <div class="flex items-center justify-between">
            <div>
              <strong class="font-semibold">
                {{ comment.author + ", " }}
              </strong>
              <span class="whitespace-nowrap text-gray-500">{{ tight ? comment.updated_at : comment.created_at }}</span>
            </div>

            <MedButton
              icon="delete"
              flat
              rounded="full"
              class="hover:bg-gray-300"
              :data-test="getTestId(`comment-list-${index}-delete-button`)"
              @click="confirmRemove(comment)"
            />
          </div>

          <ComponentIcon class="absolute right-2 -bottom-4 rotate-135 text-4xl text-gray-200 select-none">
            signal_cellular_4_bar
          </ComponentIcon>

          <div class="relative" :class="{'mt-4': !tight, 'mt-1': tight}">
            {{ comment.content }}
          </div>
        </div>

        <div v-else class="relative rounded-xl bg-gray-100" :class="{'p-4': !tight, 'p-2': tight}">
          <div class="flex items-center justify-between">
            <div>
              <strong v-if="!comment.confidential || comment.author?.length > 0" class="font-semibold">
                {{ comment.author + ", " }}
              </strong>

              <strong v-else class="font-semibold italic">Extern hinzugezogener Arzt,&nbsp;</strong>
              <span class="whitespace-nowrap text-gray-500">{{ tight ? comment.updated_at : comment.created_at }}</span>
            </div>

            <div v-if="comment.acceptance">
              <strong v-if="comment.acceptance === 'accepted'" class="font-bold text-mcgreen">
                Vorschlag akzeptiert
              </strong>

              <strong v-if="comment.acceptance === 'declined'" class="font-bold text-mcred">Vorschlag abgelehnt</strong>
            </div>
          </div>

          <div class="relative z-10" :class="{'mt-4': !tight, 'mt-1': tight}">
            {{ comment.content }}
          </div>

          <ComponentIcon class="absolute -bottom-4 left-2 -rotate-45 text-4xl text-gray-100 select-none">
            signal_cellular_4_bar
          </ComponentIcon>
        </div>
      </li>
    </ul>

    <ComponentConfirmationDialog
      ref="refConfirmRemove"
      title="Kommentar löschen?"
      content="Sind Sie sich sicher, dass Sie diesen Kommentar löschen möchten?"
      @confirmed="(comment) => remove(comment)"
    />
  </div>
</template>
